import React, { useState, useMemo } from "react";
import {
  Search,
  Nfc,
  CreditCard,
  Smartphone,
  SignalHigh,
  KeyRound,
  ArrowLeftRight,
  Receipt,
  Clock,
  Check,
  ArrowRight,
  Phone,
  BookOpen,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { guides, guideCategories, PHONE, PHONE_TEL } from "../mock";

const iconMap = {
  Nfc,
  CreditCard,
  Smartphone,
  SignalHigh,
  KeyRound,
  ArrowLeftRight,
  Receipt,
};

const HelpGuides = () => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [active, setActive] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return guides.filter((g) => {
      const matchCat = category === "All" || g.category === category;
      const matchQuery =
        !q ||
        g.title.toLowerCase().includes(q) ||
        g.summary.toLowerCase().includes(q) ||
        g.category.toLowerCase().includes(q) ||
        g.keywords.some((k) => k.includes(q));
      return matchCat && matchQuery;
    });
  }, [query, category]);

  const ActiveIcon = active ? iconMap[active.icon] : null;

  return (
    <section id="guides" className="bg-[#ece9f8] py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <p className="reveal flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-[#7c3aed] mb-4">
          <BookOpen className="w-4 h-4" /> Help Guides
        </p>
        <div className="reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <h2 className="font-display text-4xl lg:text-5xl font-semibold text-[#1e183a] leading-tight max-w-2xl">
            Step-by-step fixes, in plain English.
          </h2>
          <p className="text-[#1e183a]/60 max-w-sm leading-relaxed">
            Search our free guides for common SIM &amp; eSIM issues &mdash; or call
            us and we'll walk you through it.
          </p>
        </div>

        {/* search */}
        <div className="reveal mt-10 relative max-w-2xl">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1e183a]/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search guides — e.g. eSIM, PUK code, mobile data…"
            className="w-full rounded-full border border-[#1e183a]/12 bg-white pl-14 pr-5 py-4 text-[#1e183a] placeholder:text-[#1e183a]/40 outline-none focus:border-[#7c3aed] focus:ring-2 focus:ring-[#7c3aed]/20 transition"
          />
        </div>

        {/* category chips */}
        <div className="reveal mt-6 flex flex-wrap gap-2.5">
          {guideCategories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${
                category === c
                  ? "bg-[#7c3aed] text-white"
                  : "bg-white text-[#1e183a]/70 hover:text-[#7c3aed] border border-[#1e183a]/10"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* results */}
        {filtered.length === 0 ? (
          <div className="mt-12 rounded-3xl bg-white border border-[#1e183a]/8 p-12 text-center">
            <p className="font-display text-2xl font-semibold text-[#1e183a]">
              No guides match "{query}"
            </p>
            <p className="mt-2 text-[#1e183a]/60">
              Not listed? Our advisors can help with anything — it's completely free.
            </p>
            <a
              href={`tel:${PHONE_TEL}`}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white font-semibold px-6 py-3.5 transition-all hover:-translate-y-0.5 cta-glow"
            >
              <Phone className="w-4 h-4" strokeWidth={2.5} /> Call {PHONE}
            </a>
          </div>
        ) : (
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((g) => {
              const Icon = iconMap[g.icon];
              return (
                <button
                  key={g.id}
                  onClick={() => setActive(g)}
                  className="group text-left rounded-3xl bg-white border border-[#1e183a]/8 p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(30,24,58,0.4)] hover:border-[#7c3aed]/30"
                >
                  <div className="flex items-center justify-between">
                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#f4f2fb] text-[#7c3aed] transition-colors group-hover:bg-[#7c3aed] group-hover:text-white">
                      <Icon className="w-6 h-6" strokeWidth={2.2} />
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wide text-[#7c3aed] bg-[#f4f2fb] rounded-full px-3 py-1">
                      {g.category}
                    </span>
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[#1e183a] mt-5 leading-snug">
                    {g.title}
                  </h3>
                  <p className="mt-2.5 text-sm text-[#1e183a]/60 leading-relaxed flex-1">
                    {g.summary}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs text-[#1e183a]/45">
                      <Clock className="w-3.5 h-3.5" /> {g.time} read
                    </span>
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-[#7c3aed] group-hover:gap-2.5 transition-all">
                      Read guide <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* step-by-step modal */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg bg-white border-none rounded-3xl p-0 overflow-hidden max-h-[88vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader className="p-7 pb-5 bg-[#f4f2fb] text-left space-y-0">
                <div className="flex items-center gap-2 mb-3">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#7c3aed] text-white">
                    {ActiveIcon && <ActiveIcon className="w-5 h-5" strokeWidth={2.2} />}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#7c3aed] bg-white rounded-full px-3 py-1">
                    {active.category}
                  </span>
                </div>
                <DialogTitle className="font-display text-2xl font-semibold text-[#1e183a] leading-snug">
                  {active.title}
                </DialogTitle>
                <p className="text-sm text-[#1e183a]/60 leading-relaxed pt-2">
                  {active.summary}
                </p>
              </DialogHeader>

              <div className="p-7 pt-6">
                <ol className="space-y-5">
                  {active.steps.map((s, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="shrink-0 grid place-items-center w-8 h-8 rounded-full bg-[#7c3aed]/10 text-[#7c3aed] font-display font-semibold text-sm">
                        {i + 1}
                      </span>
                      <p className="text-[#1e183a]/80 leading-relaxed pt-0.5">{s}</p>
                    </li>
                  ))}
                </ol>

                <div className="mt-7 rounded-2xl bg-[#f4f2fb] p-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-sm text-[#1e183a]/70">
                    <Check className="w-4 h-4 text-[#7c3aed]" /> Still stuck?
                  </div>
                  <a
                    href={`tel:${PHONE_TEL}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white font-semibold text-sm px-5 py-3 transition-all hover:-translate-y-0.5"
                  >
                    <Phone className="w-4 h-4" strokeWidth={2.5} /> Call us free
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default HelpGuides;
